import { useQuery } from 'react-query';

import env from 'environments';
import { getPrintCreditsBalance } from 'modules/api/digitalServices/mailOnDemand';
// Imported for its identity, not to call it: EditorTemplate keys its legacy
// credits query on the function object itself, so invalidating that entry
// requires the same reference.
import { fetchUserCredits } from 'modules/api';
// Leaf module rather than the `modules/v2/utils` barrel, to keep this hook's
// import surface narrow. `isDentistCustomer` is a plain localStorage read — no
// hooks — so it is safe to call from anywhere, including outside a component.
import { isDentistCustomer } from 'modules/v2/utils/isDentistCustomer';

/**
 * Shared react-query key for the member's ledger credit balance
 * (GET /v1/credits/balance?creditType=print). Every surface that shows the
 * member's credits reads through this key, and every flow that changes the
 * balance (MOD pay/cancel, credit purchase) invalidates it so the header
 * updates without a reload.
 */
export const PRINT_CREDITS_BALANCE_QUERY_KEY = 'printCreditsBalance';

export const isLedgerCreditsDisplayEnabled = () => Boolean(env.LEDGER_CREDITS_DISPLAY);

/**
 * Whether THIS member's credit displays should read the DS print ledger.
 *
 * Two conditions, both required:
 *   1. LEDGER_CREDITS_DISPLAY is on.
 *   2. The member is NOT a Dentist account.
 *
 * Condition 2 exists because the legacy BBA read returns ONE polymorphic
 * number. `GET /user/credits` resolves to
 * `accountType === 'Dentist' ? dentist_guide_credits : afy_book_credits`
 * (afy-cover-editor-service `modules/auth/model.js`), so `data.data.credits`
 * is a DIFFERENT CURRENCY depending on who is asking.
 *
 * The DS ledger tracks print credits only — `LedgerCreditType` has a single
 * member, `Print` — and dentist guide credits are a deliberate, permanent
 * exclusion (universal-credits ADR-003, enforced in
 * `PurchaseLedgerGrantService`, which skips any credits property that is not
 * `afy_book_credits`). A dentist therefore has no ledger balance at all, and
 * reading one would render a fabricated number in place of their real guide
 * credits.
 *
 * Use THIS for any member-facing credit number. `isLedgerCreditsDisplayEnabled`
 * is the raw flag and answers a different question — whether the cutover is on
 * at all, not whether it applies to this member.
 */
export const shouldUsePrintLedgerDisplay = () =>
  isLedgerCreditsDisplayEnabled() && !isDentistCustomer();

export const usePrintCreditsBalance = (options = {}) =>
  useQuery(PRINT_CREDITS_BALANCE_QUERY_KEY, getPrintCreditsBalance, options);

/**
 * Refreshes every credit balance on screen, whichever source it reads from.
 *
 * A member's balance is currently cached under FOUR distinct react-query
 * entries, because surfaces were written at different times:
 *
 *   'printCreditsBalance'  the DS ledger (this hook, MOD, and every migrated surface)
 *   'getUserCredits'       legacy BBA — also written as ['getUserCredits'];
 *                          react-query v3 hashes a string and a single-element
 *                          array identically, so those are ONE entry
 *   'fetchUserCredits'     legacy BBA — a DIFFERENT string, so a SEPARATE entry
 *   [fetchUserCredits]     legacy BBA keyed by the function object itself
 *                          (EditorTemplate only)
 *
 * Any flow that changes a balance must invalidate all four, or the header and
 * the page the member is looking at will disagree until a reload. Call this
 * rather than listing keys at each site — the set has already been got wrong
 * once per call site.
 *
 * Consolidating these onto one key is worthwhile but changes cache sharing
 * while the ledger flag is still two-way, so it belongs in its own change.
 */
export const invalidateAllCreditBalances = (queryClient) => {
  queryClient.invalidateQueries(PRINT_CREDITS_BALANCE_QUERY_KEY);
  queryClient.invalidateQueries('getUserCredits');
  queryClient.invalidateQueries('fetchUserCredits');
  queryClient.invalidateQueries([fetchUserCredits]);
};

/**
 * Delays, in ms, at which {@link refreshCreditBalancesAfterUse} re-invalidates
 * after a credit-use event: immediately, then a short catch-up window.
 */
export const CREDIT_REFRESH_RETRY_DELAYS_MS = [1500, 4000];

/**
 * Refreshes every credit balance after a credit-USE event (book order, MOD
 * pay), tolerating an asynchronously-applied debit.
 *
 * {@link invalidateAllCreditBalances} alone is correct only when the debit is
 * already durable by the time the caller runs. That holds for the MOD pay
 * (synchronous ledger debit in the request) but NOT for a book order: BBA
 * returns 200 and only THEN emits `bba.books.order`, which the DS consumer
 * debits from the ledger a beat later — so an immediate refetch races the
 * debit and reads the pre-order balance, leaving the header stale exactly
 * when the member expects it to drop.
 *
 * This invalidates immediately (instant feedback for the synchronous case)
 * and re-invalidates over a short window so the header converges to the true
 * balance within a few seconds once the async debit lands. Each retry is a
 * cheap conditional refetch of one small endpoint; the window is bounded, so
 * there is no open-ended polling.
 *
 * Returns a cancel function; call it on unmount so pending timers do not fire
 * against a torn-down QueryClient.
 */
export const refreshCreditBalancesAfterUse = (queryClient) => {
  invalidateAllCreditBalances(queryClient);

  const timers = CREDIT_REFRESH_RETRY_DELAYS_MS.map((delay) =>
    setTimeout(() => invalidateAllCreditBalances(queryClient), delay),
  );

  return () => timers.forEach(clearTimeout);
};
